import React, { useState, useEffect } from "react";
import { Controller, useForm, FormProvider } from "react-hook-form";
import {
  Checkbox,
  ColorPicker,
  Icon,
  Toggle,
  Button,
  ImageMarkerPicker,
  IconMarkerPicker,
  NumberPicker,
  ButtonGrid,
  FormError,
} from "../../_components";
import { DevTool } from "@hookform/devtools";
import { useModal } from "../../_contexts";
import ReactTooltip from "react-tooltip";
import { useDispatch, useSelector } from "react-redux";
import { MarkerTemplatesActions } from "../../_actions";
import { ErrorMessage } from "@hookform/error-message";

export function MarkerTemplateForm({ template, allowFonts, prevModal }) {
  ReactTooltip.rebuild();
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  const { data: chronicle } = useSelector((state) => state.chronicle);
  const { details: world } = useSelector((state) => state.world);

  const [showAnchorButtons, setShowAnchorButtons] = useState(false);

  const { control, watch, errors, ...formMethods } = useForm({
    mode: "all",
    shouldUnregister: false,
    criteriaMode: "all",
    defaultValues: {
      name: template?.name || "",
      mode: template?.mode || "image",
      pinImg: template?.pinImg || "",
      iconGlyph: template?.iconGlyph || "",
      primaryColor: template?.primaryColor || "",
      lockAspectRatio: template?.lockAspectRatio || true,
      width: template?.width || 24,
      height: template?.height || 40,
      anchorX: template?.anchorX || 12,
      anchorY: template?.anchorY || 40,
      borderEnabled: template?.borderEnabled || false,
      borderWidth: template?.borderWidth || 1,
      borderStyle: template?.borderStyle || "solid",
      borderColor: template?.borderColor || "transparent",
      tooltipEnabled: template?.tooltipEnabled || true,
      customClassName: template?.customClassName || "",
      filter: template?.filter || "",
      chronicle: template?.chronicle || chronicle?._id || "",
      world: template?.world || world?._id || "",
      //user:::
    },
  });

  const handleCloseModal = () => {
    if (prevModal) {
      openModal(prevModal);
    } else {
      closeModal();
    }
  };

  const maxIterations = 100;
  let iterations = 0;
  const generateFilterStyle = (colorBase) => {
    if (iterations < maxIterations) {
      iterations++;
      const result = resolveColorStyle(colorBase);
      if (result.loss > LOSS_THRESHOLD) {
        generateFilterStyle(colorBase);
      } else {
        setValue("filter", result.filter);
      }
    } else {
      //Ran x iters, failed to get accurate color!
    }
  };

  const onSubmit = (formData) => {
    console.log("FormData: ", formData);
    if (typeof formData.pinImg !== "string" && formData.pinImg._id) {
      formData.pinImg = formData.pinImg._id;
    }
    if (
      typeof formData.primaryColor === "string" &&
      formData.primaryColor !== ""
    ) {
      if (template && template._id) {
        if (template.primaryColor !== formData.primaryColor) {
        }
      }
    } else {
      formData.filter = "";
    }
    if (template && template._id) {
      return dispatch(
        MarkerTemplatesActions.updateTemplate(template._id, formData)
      )
        .then((result) => {
          console.log("Save Result", result);
          handleCloseModal();
        })
        .catch((error) => {
          console.log("Error (saving)", error);
        });
    }
    return dispatch(MarkerTemplatesActions.createTemplate(formData))
      .then((result) => {
        console.log("Create Success!!", result);
        handleCloseModal();
        //handle success
      })
      .catch((error) => {
        console.log("Error (create)", error);
        //handle error
      });
  };

  const handleCancel = () => {
    handleCloseModal();
  };

  const alignAnchor = (pos) => {
    let newPos = {
      x: 0,
      y: 0,
    };
    switch (pos.toLowerCase()) {
      case "bottomcenter": {
        newPos.y = (parseFloat(formMethods.getValues("height")) * 1).toFixed(2);
        newPos.x = (parseFloat(formMethods.getValues("width")) / 2).toFixed(2);
        break;
      }
      case "bottomleft": {
        newPos.y = (parseFloat(formMethods.getValues("height")) * 1).toFixed(2);
        newPos.x = 0;
        break;
      }
      case "bottomright": {
        newPos.y = (parseFloat(formMethods.getValues("height")) * 1).toFixed(2);
        newPos.x = parseFloat(formMethods.getValues("width")).toFixed(2);
        break;
      }
      case "centercenter": {
        newPos.y = (parseFloat(formMethods.getValues("height")) / 2).toFixed(2);
        newPos.x = (parseFloat(formMethods.getValues("width")) / 2).toFixed(2);
        break;
      }
      case "centerleft": {
        newPos.y = (parseFloat(formMethods.getValues("height")) / 2).toFixed(2);
        newPos.x = 0;
        break;
      }
      case "centerright": {
        newPos.y = (parseFloat(formMethods.getValues("height")) / 2).toFixed(2);
        newPos.x = parseFloat(formMethods.getValues("width")).toFixed(2);
        break;
      }
      case "topcenter": {
        newPos.y = 0;
        newPos.x = newPos.x = (
          parseFloat(formMethods.getValues("width")) / 2
        ).toFixed(2);
        break;
      }
      case "topleft": {
        newPos.y = 0;
        newPos.x = 0;
        break;
      }
      case "topright": {
        newPos.y = 0;
        newPos.x = parseFloat(formMethods.getValues("width")).toFixed(2);
        break;
      }
    }

    formMethods.setValue("anchorX", parseFloat(newPos.x));
    formMethods.setValue("anchorY", parseFloat(newPos.y));
  };

  const tooltipID = "chronosMarkerTemplateForm";
  const tooltipIcon = "fas fa-info-circle";
  const isImageMode = watch("mode") === "image";

  return (
    <FormProvider
      control={control}
      watch={watch}
      // handleSubmit={handleSubmit}
      {...formMethods}
    >
      <form
        onSubmit={formMethods.handleSubmit(onSubmit)}
        className="chronos-form chronos-modal-form"
      >
        <div className="form-group">
          <label htmlFor="name" className="chronos-form-title">
            Name
            <sup className="required-flag">*</sup>
          </label>
          <Controller
            control={control}
            name="name"
            as="input"
            placeholder="Template Name"
            className="form-control"
            rules={{
              required: "You must specify a name for this Template",
              minLength: {
                value: 3,
                message: "The Template's name must be at least 3 characters",
              },
            }}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => <FormError message={message} />}
          />
        </div>
        {allowFonts && (
          <div className="form-group">
            <label htmlFor="mode" className="chronos-form-title">
              Marker Type
            </label>
            <Controller
              control={control}
              name="mode"
              render={({ value, onChange }) => (
                <Toggle
                  checked={value === "image"}
                  id="mode"
                  name="mode"
                  onChange={(val) => {
                    onChange(val ? "image" : "icon");
                  }}
                  onText="Image"
                  offText="Icon"
                />
              )}
            />
          </div>
        )}

        {/* ImageConfig */}
        {isImageMode && (
          <React.Fragment>
            <div className="form-group">
              <label htmlFor="pinImg" className="chronos-form-title">
                Marker Image
              </label>
              <Controller
                control={control}
                name="pinImg"
                render={({ value, onChange }) => (
                  <ImageMarkerPicker
                    value={value}
                    onChange={onChange}
                    color={watch("primaryColor")}
                    // anchorX={watch('anchorX')}
                    // anchorY={watch('anchorY')}
                  />
                )}
              />
            </div>
            <div className="chronos-form-section-horizontal">
              {/* Size */}
              <div className="form-group">
                <p className="chronos-section-title">
                  Image Size&nbsp;
                  <Icon
                    icon={tooltipIcon}
                    data-tip="This controls the image's dimensions, but may cause stretching or squishing"
                    data-for={tooltipID}
                  />
                </p>
                <label htmlFor="width" className="chronos-form-title">
                  Width
                </label>
                <Controller
                  control={control}
                  name="width"
                  render={({ value, onChange }) => {
                    const handleChange = (val) => {
                      console.log("Width", val);
                      const isLockedAspect =
                        formMethods.getValues("lockAspectRatio");
                      if (isLockedAspect) {
                        const dimensions = formMethods.getValues([
                          "width",
                          "height",
                        ]);
                        const ratio = parseFloat(
                          (
                            (dimensions.height / dimensions.width) *
                            val
                          ).toFixed(2)
                        );
                        console.log("Ratio", ratio);
                        formMethods.setValue("height", ratio);
                      }
                      onChange(val);
                    };
                    return (
                      <NumberPicker
                        value={value}
                        onChange={handleChange}
                        min={1}
                        max={120}
                      />
                    );
                  }}
                />
                <label htmlFor="height" className="chronos-form-title">
                  Height
                </label>
                <Controller
                  control={control}
                  name="height"
                  render={({ value, onChange }) => (
                    <NumberPicker
                      value={value}
                      onChange={onChange}
                      min={1}
                      max={120}
                      disabled={watch("lockAspectRatio")}
                    />
                  )}
                />
                <label className="chronos-form-title">
                  {/* <div> */}
                  Lock Aspect Ratio?&nbsp;
                  <Icon
                    icon={tooltipIcon}
                    data-for={tooltipID}
                    data-tip="This locks the size of the height and width of the image. This is useful to prevent issues with stretching."
                  />
                  {/* </div> */}
                  <Controller
                    control={control}
                    name="lockAspectRatio"
                    render={({ value, onChange }) => {
                      return (
                        <Checkbox value={value} onChange={onChange} custom />
                      );
                    }}
                  />
                </label>
              </div>

              {/* Anchor */}
              <div className="form-group">
                <p className="chronos-section-title">
                  Anchor Position&nbsp;
                  <Icon
                    icon={tooltipIcon}
                    data-tip={`<div>
                        This controls the spot on the image that indicates how
                        the marker will be placed relative to the desired
                        position. For 'pin' style images, the anchor should be
                        halfway horizontally and at the bottom vertically. For
                        round or square shaped images, the anchor should be
                        positioned in the center both vertically and
                        horizontally. <span class='ct-flip'>T</span>
                      </div>`}
                    data-html={true}
                    data-multiline={true}
                    data-for={tooltipID}
                  />
                </p>
                <label htmlFor="anchorX" className="chronos-form-title">
                  Horizontal Position (X)
                </label>
                <Controller
                  control={control}
                  name="anchorX"
                  render={({ value, onChange }) => (
                    <NumberPicker
                      value={value}
                      onChange={onChange}
                      min={0}
                      max={120}
                    />
                  )}
                />
                <label htmlFor="anchorY" className="chronos-form-title">
                  Vertical Position (Y)
                </label>
                <Controller
                  control={control}
                  name="anchorY"
                  render={({ value, onChange }) => (
                    <NumberPicker
                      value={value}
                      onChange={onChange}
                      min={0}
                      max={120}
                    />
                  )}
                />
                <label
                  onClick={() => setShowAnchorButtons(!showAnchorButtons)}
                  className="chronos-form-title"
                >
                  <span>
                    Quick Align&nbsp;
                    <Icon
                      icon={tooltipIcon}
                      data-for={tooltipID}
                      data-tip="You can easily align the Anchor to a preset position."
                    />
                  </span>

                  <Icon
                    icon={
                      showAnchorButtons
                        ? "far fa-chevron-up fa-fw"
                        : "far fa-chevron-down fa-fw"
                    }
                    style={{ marginLeft: "1rem" }}
                  />
                </label>
                {showAnchorButtons && (
                  <AnchorAlignmentButtons handler={alignAnchor} />
                )}
              </div>
            </div>
          </React.Fragment>
        )}

        {/* IconConfig */}
        {!isImageMode && (
          <React.Fragment>
            <Controller
              control={control}
              name="iconGlyph"
              render={({ value, onChange }) => (
                <IconMarkerPicker value={value} onChange={onChange} />
              )}
            />
          </React.Fragment>
        )}

        <div className="form-group">
          <label htmlFor="primaryColor" className="chronos-form-title">
            Color Shift&nbsp;
            <Icon
              icon={tooltipIcon}
              data-tip="Applies a hue shift to the image. For best effect, it's best to select a marker that is flat white or black. Other colors and shadows on the image may result in an undesired effect."
              data-for={tooltipID}
            />
          </label>
          <Controller
            control={control}
            name="primaryColor"
            render={({ value, onChange }) => (
              <ColorPicker value={value} onChange={onChange} colorMode="hsl" />
            )}
          />
        </div>

        <div className="form-group">
          <label htmlFor="tooltipEnabled" className="chronos-form-title">
            Enable Tooltip?
            <Controller
              control={control}
              name="tooltipEnabled"
              render={({ value, onChange }) => (
                <Checkbox custom value={value} onChange={onChange} />
              )}
            />
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="customClassName" className="chronos-form-title">
            Custom CSS Class
          </label>
          <Controller
            control={control}
            name="customClassName"
            as="input"
            className="form-control"
            autoComplete="off"
          />
        </div>

        <div className="form-controls">
          &nbsp;
          {/* <Button color="secondary" onClick={handleCancel}>
            <Icon icon="far fa-ban fa-fw" />
            &nbsp;Cancel
          </Button> */}
          {template !== undefined && (
            <Button color="primary" type="submit">
              <Icon icon="far fa-save fa-fw" />
              &nbsp;Update
            </Button>
          )}
          {!template && (
            <Button color="primary" type="submit">
              <Icon icon="far fa-plus fa-fw" />
              &nbsp;Create
            </Button>
          )}
        </div>
        <ReactTooltip
          id={tooltipID}
          effect="solid"
          className="chronos-tooltip"
        />
        <DevTool control={control} />
      </form>
    </FormProvider>
  );
}

function AnchorAlignmentButtons({ handler: alignAnchor }) {
  return (
    <ButtonGrid rows={3} cols={3}>
      <Button
        size="xs"
        color="secondary"
        onClick={() => alignAnchor("topleft")}
      >
        <Icon
          icon="far fa-arrow-up fa-fw"
          style={{ transform: "rotate(-45deg)" }}
        />
      </Button>
      <Button
        size="xs"
        color="secondary"
        onClick={() => alignAnchor("topcenter")}
      >
        <Icon icon="far fa-arrow-up fa-fw" />
      </Button>
      <Button
        size="xs"
        color="secondary"
        onClick={() => alignAnchor("topright")}
      >
        <Icon
          icon="far fa-arrow-up fa-fw"
          style={{ transform: "rotate(45deg)" }}
        />
      </Button>
      <Button
        size="xs"
        color="secondary"
        onClick={() => alignAnchor("centerleft")}
      >
        <Icon
          icon="far fa-arrow-up fa-fw"
          style={{ transform: "rotate(-90deg)" }}
        />
      </Button>
      <Button
        size="xs"
        color="secondary"
        onClick={() => alignAnchor("centercenter")}
      >
        <Icon icon="far fa-dot-circle fa-fw" />
      </Button>
      <Button
        size="xs"
        color="secondary"
        onClick={() => alignAnchor("centerright")}
      >
        <Icon
          icon="far fa-arrow-up fa-fw"
          style={{ transform: "rotate(90deg)" }}
        />
      </Button>
      <Button
        size="xs"
        color="secondary"
        onClick={() => alignAnchor("bottomleft")}
      >
        <Icon
          icon="far fa-arrow-up fa-fw"
          style={{ transform: "rotate(-135deg)" }}
        />
      </Button>
      <Button
        size="xs"
        color="secondary"
        onClick={() => alignAnchor("bottomcenter")}
      >
        <Icon
          icon="far fa-arrow-up fa-fw"
          style={{ transform: "rotate(180deg)" }}
        />
      </Button>
      <Button
        size="xs"
        color="secondary"
        onClick={() => alignAnchor("bottomright")}
      >
        <Icon
          icon="far fa-arrow-up fa-fw"
          style={{ transform: "rotate(135deg)" }}
        />
      </Button>
    </ButtonGrid>
  );
}
